'use client';

import posthog from 'posthog-js';

/**
 * Typed product analytics events. Wraps `posthog.capture` so every call site
 * uses a known event name with a documented property shape. PostHog is only
 * initialised inside <PostHogProvider> (see lib/posthog.tsx); calling these
 * before init or for opted-out users is a safe no-op.
 *
 * `subscription_activated` is intentionally absent here — it is emitted
 * server-side from the Stripe webhook (see lib/analytics-server.ts) so it
 * reflects Stripe's truth rather than an optimistic client redirect.
 */
export type AnalyticsEventProperties = {
  signed_up: { method: 'email' | 'google' };
  brand_onboarding_completed: { skipped: boolean };
  voice_onboarding_completed: { skipped: boolean };
  generation_started: {
    task_id?: string;
    generation_mode?: string;
    format?: string;
    source?: 'topic' | 'file' | 'youtube';
  };
  generation_completed: { task_id?: string; slide_id?: string };
  generation_failed: { task_id?: string; error_message?: string };
  audit_requested: { business_url?: string; request_id?: string };
  checkout_started:
    | { type: 'subscription'; plan_id: string }
    | { type: 'credits'; dollar_amount: number; credits: number };
};

export type AnalyticsEvent = keyof AnalyticsEventProperties;

export function trackEvent<E extends AnalyticsEvent>(
  event: E,
  properties: AnalyticsEventProperties[E],
): void {
  try {
    posthog.capture(event, properties);
  } catch (error) {
    // Never let analytics break a user flow.
    console.warn(`[analytics] Failed to capture "${event}":`, error);
  }
}
